import posthog from 'posthog-js';

/**
 * Determines the PostHog persistence strategy based on the current cookie string.
 *
 * If a PostHog cookie (`ph_*_posthog=`) is present, persistence will use both `localStorage` and cookies.
 * Otherwise, it falls back to in-memory persistence to avoid creating new tracking cookies.
 *
 * @param cookieString - The full `document.cookie` string.
 * @returns The persistence mode to use when initializing PostHog.
 */
export function getPostHogPersistence(cookieString: string): 'localStorage+cookie' | 'memory' {
  const hasPostHogCookie: boolean = cookieString
    .split(';')
    .some(
      (cookie) =>
        cookie.trim().startsWith('ph_') &&
        cookie.includes('_posthog=')
    );

  return hasPostHogCookie ? 'localStorage+cookie' : 'memory';
}

/**
 * Initializes the PostHog client if required environment variables are present.
 *
 * This function is executed once at module load time.
 */
export function initPostHog() {
  const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

  if (!apiKey || !apiHost) return;

  const persistence: 'localStorage+cookie' | 'memory' = getPostHogPersistence(document.cookie);

  posthog.init(apiKey, {
    api_host: apiHost,
    persistence,
    autocapture: false,
    disable_session_recording: true,
    disable_surveys: true,
  });
}

initPostHog();

export default posthog;